import '@mui/material/FormHelperText';
import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

/**
 * Add new variables to the Theme and ThemeOptions for
 * using in forms elements (ex.: form create site, warning text)
 */
declare module '@mui/material/FormHelperText' {
  interface FormHelperTextPropsVariantOverrides {
    warning: true;
    info: true;
  }
}

// CONSTANTS
export const PRIMARY_BLUE = '47, 72, 92';
export const PRIMARY_GREEN = '158, 192, 63';
export const PRIMARY_WHITE = '229, 244, 242';
export const SECONDARY_DARKBLUE = '37, 81, 118';
export const SECONDARY_BLUE = '114, 170, 181';
export const SECONDARY_LIGHTBLUE = '161, 219, 211';
export const SECONDARY_GREEN = '150, 218, 138';
export const SECONDARY_LIGHTGREEN = '189, 217, 115';
export const SECONDARY_BROWN = '144, 103, 96';

export const INDICATOR_NORMAL = 'green';
export const INDICATOR_WARNING = 'orange';
export const INDICATOR_ERROR = 'red';
export const INDICATOR_CRITICAL = 'darkred';

const themeOptions = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#9ec03f',
    },
    secondary: {
      main: '#9c27b0',
    },
    text: {
      primary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Poppins Regular", sans-serif',
  },
  components: {
    MuiAppBar: { styleOverrides: { root: { boxShadow: 'none' } } },
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          '&:last-child': {
            borderBottom: '1px solid rgb(248, 248, 248)',
          },
          '&:nth-of-type(even)': {
            backgroundColor: 'rgb(248, 248, 248)',
            color: 'rgb(153, 153, 153)',
          },
          '&:nth-of-type(odd)': {
            borderBottom: '1px solid rgb(248, 248, 248)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgb(248, 248, 248)',
        },
      },
      variants: [
        {
          props: { variant: 'head' },
          style: {
            color: 'rgb(153, 153, 153)',
          },
        },
      ],
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(248, 248, 248)',
          fontWeight: 'bold',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          textTransform: 'none',
          boxShadow: 'none',
          padding: '8px 24px',
          fontWeight: 'normal',
        },
        contained: {
          color: '#fff',
          backgroundColor: `rgba(${PRIMARY_GREEN}, 0.9)`, // Normal: 0.9 opacity
          '&:hover': {
            backgroundColor: `rgba(${PRIMARY_GREEN}, 1)`, // Hover: 1 opacity
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Just for InputMultiType with error
          '.input-multi-type.MuiFormControl-root &.Mui-error': {
            position: 'absolute',
            top: 54,
            left: 0,
            zIndex: 10,
          },
        }),
      },
      variants: [
        {
          props: { variant: 'warning' },
          style: {
            backgroundColor: '#FFF9C4',
            color: '#666666',
            borderRadius: 4,
            padding: '4px 8px',
            marginBottom: '15px !important',
          },
        },
        {
          props: { variant: 'info' },
          style: {
            backgroundColor: '#F5F5F5',
            color: '#757575',
            borderRadius: 4,
            padding: '4px 8px',
          },
        },
      ],
    },

    // Styles for InputMultiType start
    MuiInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.input-multi-type &': {
            marginTop: 0,
            border: `1px solid rgba(0, 0, 0, 0.25)`,
            borderRadius: 4,
            padding: '11px 14px',
            position: 'relative',
            transition: theme.transitions.create(
              ['border-color', 'box-shadow'],
              {
                duration: theme.transitions.duration.short,
                easing: theme.transitions.easing.easeInOut,
              }
            ),

            '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused):not(:has(.MuiInputBase-root.Mui-focused))':
              {
                borderColor: 'rgba(0, 0, 0, 0.55)',
              },
            '&.Mui-focused': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
            },
            '&.Mui-error': {
              borderColor: theme.palette.error.main,
              boxShadow: `none`,
              marginBottom: '1.2rem',
            },
            '&.Mui-focused.Mui-error': {
              borderColor: theme.palette.error.main,
              boxShadow: `0 0 0 1px ${theme.palette.error.main}`,
            },
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
              opacity: '0.3',
            },
            '&::before, &::after': {
              display: 'none',
            },
          },
        }),
      },
    },

    MuiFormControl: {
      styleOverrides: {
        root: {
          '.input-multi-type &': {
            width: '100%',
            position: 'relative',
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.input-multi-type &': {
            fontSize: '1.5em',
            padding: '0 5px',
            top: '1.15em',
            left: '-0.25em',

            transition: theme.transitions.create(
              ['top', 'font-size', 'color', 'background-color', 'transform'],
              {
                duration: theme.transitions.duration.shortest,
                easing: theme.transitions.easing.easeOut,
              }
            ),
            '&.Mui-focused, &.MuiFormLabel-filled': {
              fontSize: '1.2em',
              top: 0,
              backgroundColor: '#fff',
            },
          },
        }),
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.input-multi-type &': {
            transition: theme.transitions.create(['opacity', 'transform'], {
              duration: theme.transitions.duration.shortest,
              easing: theme.transitions.easing.easeInOut,
            }),

            '&.Mui-disabled': {
              opacity: 0,
              pointerEvents: 'none',
              transform: 'scale(0.95)',
            },
          },
        }),
      },
    },
    // Styles for InputMultiType end
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          backgroundColor: 'var(--list-data-grid-t-header)',
          '&[data-field="__detail_panel_toggle__"]': {
            backgroundColor: 'var(--list-data-grid-t-header)',
          },
        },
      },
    },
  },
});

export default themeOptions;
